import type { ChordQuality } from './chords';
import { CHORD_INTERVALS, QUALITY_DISPLAY } from './chords';
import { INTERVAL_BY_LABEL, INTERVAL_BY_SEMITONE } from './intervals';
import { SCALES, ScaleId } from './scales';
import { SHARP_KEYS } from './notes';
import { transposeNote } from '../utils/musicTheory';

export type DiatonicArpeggioSize = 'triad' | 'seventh';

/**
 * The 9 standard arpeggio qualities exposed in Free-pick mode. Re-uses the
 * existing ChordQuality type from data/chords.ts so display labels and
 * interval formulas stay in lockstep with the chord library.
 */
export const ARPEGGIO_QUALITIES = [
  'maj', 'min', 'dim', 'aug',
  'maj7', 'min7', 'dom7', 'min7b5', 'dim7',
] as const satisfies ReadonlyArray<ChordQuality>;

export type ArpeggioQuality = typeof ARPEGGIO_QUALITIES[number];

/**
 * Active arpeggio overlay selection. Exactly one of:
 *   • diatonic — degree-anchored within the active scale (chip count = scale length)
 *   • free     — root + quality, can sit outside the active key
 *   • null     — overlay is off
 */
export type ArpeggioSelection =
  | { mode: 'diatonic'; degree: number; size: DiatonicArpeggioSize }
  | { mode: 'free'; root: string; quality: ArpeggioQuality }
  | null;

function qualityToSemitones(quality: ChordQuality): Set<number> {
  const labels = CHORD_INTERVALS[quality];
  return new Set(labels.map(l => INTERVAL_BY_LABEL[l].semitones));
}

/**
 * If the offset set exactly equals one of the 9 ARPEGGIO_QUALITIES'
 * interval-formula sets, return that quality. Otherwise return null and let
 * callers fall back to formatIntervalSet for display.
 */
export function matchQuality(offsets: Set<number>): ChordQuality | null {
  for (const q of ARPEGGIO_QUALITIES) {
    const expected = qualityToSemitones(q);
    if (expected.size !== offsets.size) continue;
    let allMatch = true;
    for (const s of expected) {
      if (!offsets.has(s)) { allMatch = false; break; }
    }
    if (allMatch) return q;
  }
  return null;
}

/**
 * Pretty-print an interval set for the chip-label fallback (used when
 * matchQuality returns null). Sorts ascending and joins with '-'.
 *   {0, 4, 9} → "1-3-6"
 *   {0, 3, 6, 10} → "1-b3-b5-b7"
 */
export function formatIntervalSet(offsets: Set<number>): string {
  return [...offsets]
    .sort((a, b) => a - b)
    .map(s => INTERVAL_BY_SEMITONE[s]?.label ?? String(s))
    .join('-');
}

/**
 * Compute the semitone offsets (relative to the degree root) for a diatonic
 * arpeggio at the given degree of the given scale. Returns the degree root's
 * semitone offset from the scale root plus the offset set, or null if the
 * degree is out of range for the scale.
 *
 *   • triad:   offsets at scale-steps [0, +2, +4]
 *   • seventh: also includes +6
 *   • all wrapped (mod scaleLength) to support 5/6/7/8-note scales
 */
export function getDiatonicArpeggioOffsets(
  scaleIntervals: number[],
  degree: number,
  size: DiatonicArpeggioSize,
): { degreeRootSemi: number; offsets: Set<number> } | null {
  const N = scaleIntervals.length;
  const d = degree - 1;
  if (d < 0 || d >= N) return null;
  const degreeRootSemi = scaleIntervals[d];
  const offsets = new Set<number>([
    0,
    (scaleIntervals[(d + 2) % N] - degreeRootSemi + 12) % 12,
    (scaleIntervals[(d + 4) % N] - degreeRootSemi + 12) % 12,
  ]);
  if (size === 'seventh') {
    offsets.add((scaleIntervals[(d + 6) % N] - degreeRootSemi + 12) % 12);
  }
  return { degreeRootSemi, offsets };
}

/**
 * Display label for the active arpeggio overlay (used by ArpeggioButton and
 * the chip "selected" state in ArpeggioSheet). Returns e.g. "Cmaj7", "Em",
 * "F#ø7", or for non-standard interval sets a fallback like "C 1-3-6".
 *
 * Returns '' if a diatonic selection is missing the scale context it needs to
 * resolve to a chord.
 */
export function arpeggioLabel(
  selection: NonNullable<ArpeggioSelection>,
  scaleRootNote: string | null,
  scaleId: ScaleId | null,
): string {
  if (selection.mode === 'free') {
    return `${selection.root}${QUALITY_DISPLAY[selection.quality]}`;
  }

  // diatonic
  if (!scaleRootNote || !scaleId) return '';
  const scale = SCALES[scaleId]?.intervals;
  if (!scale) return '';

  const result = getDiatonicArpeggioOffsets(scale, selection.degree, selection.size);
  if (!result) return '';
  const { degreeRootSemi, offsets } = result;

  const useSharps = SHARP_KEYS.has(scaleRootNote);
  const rootDisplay = transposeNote(scaleRootNote, degreeRootSemi, useSharps);
  const matched = matchQuality(offsets);
  if (matched) {
    return `${rootDisplay}${QUALITY_DISPLAY[matched]}`;
  }
  // Fallback for non-standard interval sets (e.g. pentatonic / 8-note scales).
  return `${rootDisplay} ${formatIntervalSet(offsets)}`;
}
